import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Check,
  CheckCircle2,
  Lock,
  MessageCircle,
  PanelRightClose,
  PanelRightOpen,
  Phone,
  RotateCcw,
  StickyNote,
  User,
  UserPlus,
  X,
} from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useFirstCompany } from '@/api/profile'
import { useContactsMap } from '@/hooks/useContactsMap'
import { useCallActions } from '@/hooks/useCallActions'
import { useSelectedCallerId } from '@/hooks/useSelectedCallerId'
import { formatPhoneDisplay } from '@/lib/phoneValidation'
import { useUIStore, type EchangeCible } from '@/stores/useUIStore'
import type { MessageChannel } from '@/types/sms'
import { ConversationTimeline } from './ConversationTimeline'
import { nomLigne, useActionFil, useEtatFil, useInboxLines } from '@/api/inbox'
import { useTeamMembers, type TeamMember } from '@/api/agents'
import { useProfile } from '@/api/profile'
import { usePlanRights } from '@/hooks/usePlanRights'
import { useColleguesQuiEcrivent, useSignalerFrappe } from '@/hooks/useFrappe'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useContactTimeline } from '@/api/timeline'
import { InCallPanel } from './InCallPanel'
import { SMSComposer } from './SMSComposer'

interface Props {
  phoneNumber: string
  darkMode?: boolean
  /** Ferme le fil (retour à la liste sur mobile). */
  onClose?: () => void
  /** Bouton qui affiche/masque la fiche à droite. Faux quand aucune fiche n'est rendue. */
  showProfileToggle?: boolean
  /** La ligne du fil (boîte de réception par ligne) ; null : toutes les lignes. */
  line?: string | null
  /** Ouvre le fil de la même personne sur une autre ligne. */
  onSelectLine?: (line: string) => void
  /** Opened on a search result: the exchange to scroll to and highlight. */
  cible?: EchangeCible | null
}

function initialesDe(nom: string): string {
  const mots = nom.trim().split(/\s+/).filter((m) => /\p{L}/u.test(m[0] || ''))
  return mots
    .slice(0, 2)
    .map((m) => m[0].toUpperCase())
    .join('')
}

/**
 * Fil unifié d'une personne : appels (manqués compris, résumé IA, audio),
 * textos (photos comprises), messages vocaux et notes, dans l'ordre.
 *
 * En-tête : avatar, nom ou numéro, appeler, afficher la fiche.
 * En bas : l'appel en cours avec cette personne, puis le composer riche.
 */
export function ContactDetail({
  phoneNumber,
  darkMode = false,
  onClose,
  showProfileToggle = true,
  line = null,
  onSelectLine,
  cible = null,
}: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { getContactByNumber, getDisplayName, getAvatarUrl } = useContactsMap()
  const { makeOutboundCall } = useCallActions()
  const selectedCallerId = useSelectedCallerId()
  const profilePanelCollapsed = useUIStore((s) => s.profilePanelCollapsed)
  const toggleProfilePanel = useUIStore((s) => s.toggleProfilePanelCollapsed)
  const { data: company } = useFirstCompany()
  const whatsappEnabled = company?.whatsapp_enabled ?? false
  const [channel, setChannel] = useState<MessageChannel>('sms')
  // Texto ou note interne (plan Team).
  const [mode, setMode] = useState<'sms' | 'note'>('sms')

  // Collaboration d'équipe (plan Team) : grisée avec « Inclus dans Team » sinon.
  const { allowed } = usePlanRights()
  const equipe = allowed('collaboration_equipe')
  const inclusDansTeam = t('plan.includedIn', { plan: 'Team' })
  const { data: profile } = useProfile()
  const { data: membres = [] } = useTeamMembers()
  // Attribuer et Terminé n'ont de sens que pour une conversation de ligne,
  // pas pour le fil de toutes les lignes ouvert depuis la fiche.
  const { data: etat } = useEtatFil(phoneNumber, line, equipe)
  const actionFil = useActionFil(phoneNumber, line)
  const ecrivent = useColleguesQuiEcrivent(phoneNumber, line)
  const signalerFrappe = useSignalerFrappe(phoneNumber, line, equipe)
  const nomMembre = (m: TeamMember) => `${m.user_first_name || ''} ${m.user_last_name || ''}`.trim() || m.user_email

  const contact = getContactByNumber(phoneNumber)
  const formattedPhone = formatPhoneDisplay(phoneNumber)
  // Sans fiche (ou fiche sans nom) : le numéro, lisible.
  const nom = contact ? getDisplayName(phoneNumber) : ''
  const displayName = nom && nom !== phoneNumber ? nom : formattedPhone
  const avatarUrl = getAvatarUrl(phoneNumber)
  const initiales = displayName !== formattedPhone ? initialesDe(displayName) : ''

  // La ligne du fil et les autres lignes où l'on a parlé à cette personne.
  const lignesEntreprise = useInboxLines()
  const nomDe = (numero: string) => {
    const l = lignesEntreprise.find((x) => x.number === numero)
    return l ? nomLigne(l, formatPhoneDisplay) : formatPhoneDisplay(numero)
  }
  const plusieursLignes = lignesEntreprise.length > 1
  const { data: fil } = useContactTimeline(phoneNumber, line, cible?.at ?? null)
  const autresLignes = plusieursLignes ? (fil?.pages[0]?.lines ?? []).filter((l) => l !== line) : []
  const lienLigne = darkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700'

  const iconButton = cn(
    'p-2 rounded-lg transition-colors',
    darkMode
      ? 'hover:bg-slate-800 text-slate-400 hover:text-white'
      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700',
  )

  const handleSent = () => {
    queryClient.invalidateQueries({ queryKey: ['contact-timeline', phoneNumber] })
    queryClient.invalidateQueries({ queryKey: ['inbox'] })
  }

  const puce = (actif: boolean) =>
    cn(
      'px-2 py-0.5 rounded text-[11px] font-medium transition-all flex items-center gap-1',
      actif
        ? darkMode
          ? 'bg-slate-700/60 text-white'
          : 'bg-slate-200 text-slate-800'
        : darkMode
          ? 'text-slate-500 hover:text-slate-300'
          : 'text-gray-400 hover:text-gray-600',
    )

  // Au-dessus du champ : Texto (ou SMS / WhatsApp si l'add-on est actif) et
  // Note interne (plan Team).
  const channelPicker = (
    <div className="flex items-center gap-1 px-4 pt-2">
      {whatsappEnabled ? (
        (['sms', 'whatsapp'] as const).map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => {
            setChannel(key)
            setMode('sms')
          }}
          className={cn(
            'px-2 py-0.5 rounded text-[11px] font-medium transition-all flex items-center gap-1',
            mode === 'sms' && channel === key
              ? darkMode
                ? 'bg-slate-700/60 text-white'
                : 'bg-slate-200 text-slate-800'
              : darkMode
                ? 'text-slate-500 hover:text-slate-300'
                : 'text-gray-400 hover:text-gray-600',
          )}
        >
          {key === 'whatsapp' && <MessageCircle className="w-3 h-3 text-green-500" />}
          {key === 'sms'
            ? t('sms.channels.sms', 'SMS')
            : t('sms.channels.whatsapp', 'WhatsApp')}
        </button>
        ))
      ) : (
        <button type="button" onClick={() => setMode('sms')} className={puce(mode === 'sms')}>
          {t('inbox.team.text', 'Texto')}
        </button>
      )}
      <button
        type="button"
        onClick={() => setMode('note')}
        disabled={!equipe}
        title={equipe ? undefined : inclusDansTeam}
        className={cn(
          puce(false),
          mode === 'note' &&
            (darkMode ? 'bg-slate-700/60 text-white' : 'bg-slate-200 text-slate-800'),
          !equipe && 'opacity-60 cursor-not-allowed hover:text-inherit',
        )}
      >
        {equipe ? <StickyNote className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
        {t('inbox.team.internalNote', 'Note interne')}
      </button>
    </div>
  )

  const boutonEquipe = cn(
    'inline-flex items-center gap-1.5 h-8 px-2 rounded-lg text-[12px] font-medium transition-colors',
    darkMode
      ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
      : 'text-slate-600 hover:bg-gray-100 hover:text-slate-900',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent',
  )
  const assigne = etat?.assigned_to ?? null
  const attribuer = (userId: number | null) => actionFil.mutate({ action: 'assign', user_id: userId })

  // En-tête : Attribuer et Terminé / Rouvrir, pour une conversation de ligne.
  const actionsEquipe = line ? (
    equipe ? (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={boutonEquipe}
              title={assigne ? t('inbox.team.assignedTo', 'Attribuée à {{name}}', { name: assigne.name }) : undefined}
            >
              {assigne ? (
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full text-[9.5px] font-semibold',
                    darkMode ? 'bg-indigo-500/30 text-indigo-100' : 'bg-indigo-100 text-indigo-700',
                  )}
                >
                  {assigne.initials}
                </span>
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              <span className="hidden lg:inline max-w-[120px] truncate">
                {assigne ? assigne.name : t('inbox.team.assign', 'Attribuer')}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {profile?.id != null && assigne?.user_id !== profile.id && (
              <>
                <DropdownMenuItem onSelect={() => attribuer(profile.id ?? null)}>
                  {t('inbox.team.assignMe', "Me l'attribuer")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {membres.map((m) => (
              <DropdownMenuItem key={m.user_id} onSelect={() => attribuer(m.user_id)}>
                <span className="flex-1 truncate">{nomMembre(m)}</span>
                {assigne?.user_id === m.user_id && <Check className="h-3.5 w-3.5" />}
              </DropdownMenuItem>
            ))}
            {assigne && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => attribuer(null)}>
                  {t('inbox.team.unassign', 'Retirer')}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {etat?.done ? (
          <button
            type="button"
            onClick={() => actionFil.mutate({ action: 'reopen' })}
            disabled={actionFil.isPending}
            className={boutonEquipe}
            title={etat.done_by ? t('inbox.team.doneBy', 'Terminée par {{name}}', { name: etat.done_by.name }) : undefined}
          >
            <RotateCcw className="h-4 w-4" />
            <span className="hidden lg:inline">{t('inbox.team.reopen', 'Rouvrir')}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => actionFil.mutate({ action: 'done' })}
            disabled={actionFil.isPending}
            className={boutonEquipe}
          >
            <CheckCircle2 className="h-4 w-4" />
            <span className="hidden lg:inline">{t('inbox.team.done', 'Terminé')}</span>
          </button>
        )}
      </>
    ) : (
      <>
        <button type="button" disabled className={boutonEquipe} title={inclusDansTeam}>
          <Lock className="h-3.5 w-3.5" />
          <span className="hidden lg:inline">{t('inbox.team.assign', 'Attribuer')}</span>
        </button>
        <button type="button" disabled className={boutonEquipe} title={inclusDansTeam}>
          <Lock className="h-3.5 w-3.5" />
          <span className="hidden lg:inline">{t('inbox.team.done', 'Terminé')}</span>
        </button>
      </>
    )
  ) : null

  return (
    <div
      className={cn(
        'flex-1 flex flex-col h-full overflow-hidden min-w-0',
        darkMode ? 'bg-slate-950' : 'bg-slate-50',
      )}
    >
      <header
        className={cn(
          'flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b',
          darkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white',
        )}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Avatar className="w-8 h-8 flex-shrink-0">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
            <AvatarFallback
              className={cn(
                'text-[11px] font-semibold',
                darkMode ? 'bg-slate-700 text-slate-200' : 'bg-gray-200 text-gray-600',
              )}
            >
              {initiales || <User className="w-4 h-4" />}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p
              className={cn(
                'text-[13px] font-medium truncate',
                darkMode ? 'text-white' : 'text-slate-900',
              )}
            >
              {displayName}
            </p>
            {(displayName !== formattedPhone || (plusieursLignes && line)) && (
              <p
                className={cn(
                  'text-[11px] truncate',
                  darkMode ? 'text-slate-500' : 'text-slate-400',
                )}
              >
                {displayName !== formattedPhone && formattedPhone}
                {displayName !== formattedPhone && plusieursLignes && line && ' · '}
                {plusieursLignes && line && t('inbox.via', 'via {{line}}', { line: nomDe(line) })}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {actionsEquipe}
          <button
            type="button"
            onClick={() => void makeOutboundCall(phoneNumber, line || selectedCallerId || undefined)}
            className={iconButton}
            title={t('phone.call', 'Appeler')}
          >
            <Phone className="h-4 w-4" />
          </button>
          {showProfileToggle && (
            <button
              type="button"
              onClick={toggleProfilePanel}
              className={cn(
                'hidden md:inline-flex',
                iconButton,
                !profilePanelCollapsed &&
                  (darkMode ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700'),
              )}
              title={
                profilePanelCollapsed
                  ? t('sms.showContact', 'Voir le contact')
                  : t('sms.hideContact', 'Masquer le contact')
              }
            >
              {profilePanelCollapsed ? (
                <PanelRightOpen className="h-4 w-4" />
              ) : (
                <PanelRightClose className="h-4 w-4" />
              )}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className={iconButton}
              title={t('common.close', 'Fermer')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      {autresLignes.length > 0 && onSelectLine && (
        <div
          className={cn(
            'flex-shrink-0 flex flex-col gap-0.5 px-3 py-1.5 border-b text-[11.5px]',
            darkMode ? 'border-slate-800 bg-slate-900/60 text-slate-400' : 'border-slate-200 bg-slate-50 text-slate-500',
          )}
        >
          {autresLignes.length === 1 ? (
            <p className="flex items-center gap-1.5 min-w-0">
              <span className="truncate">
                {t('inbox.otherConversation', 'Vous avez aussi une conversation avec {{name}} sur la ligne {{line}}.', {
                  name: displayName,
                  line: nomDe(autresLignes[0]),
                })}
              </span>
              <button
                type="button"
                onClick={() => onSelectLine(autresLignes[0])}
                className={cn('flex-shrink-0 font-medium', lienLigne)}
              >
                {t('inbox.openIt', "L'ouvrir")}
              </button>
            </p>
          ) : (
            // Plusieurs lignes : une seule phrase, chaque nom de ligne ouvre sa conversation.
            <p className="min-w-0">
              {t('inbox.otherConversations', 'Vous avez aussi des conversations avec {{name}} sur les lignes', {
                name: displayName,
              })}{' '}
              {autresLignes.map((l, i) => (
                <span key={l}>
                  {i > 0 && ', '}
                  <button type="button" onClick={() => onSelectLine(l)} className={cn('font-medium', lienLigne)}>
                    {nomDe(l)}
                  </button>
                </span>
              ))}
              .
            </p>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0">
        <ConversationTimeline phoneNumber={phoneNumber} line={line} darkMode={darkMode} cible={cible} />
      </div>

      {/* In-call panel (rendu seulement si appel actif avec ce contact) */}
      <InCallPanel phoneNumber={phoneNumber} darkMode={darkMode} />

      {/* « Noah écrit… » : un collègue écrit dans ce même fil. */}
      <AnimatePresence initial={false}>
        {ecrivent.length > 0 && (
          <motion.p
            key="ecrit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            aria-live="polite"
            className={cn(
              'flex-shrink-0 px-4 py-1 text-[11.5px] italic',
              darkMode ? 'text-slate-400 bg-slate-950' : 'text-slate-500 bg-slate-50',
            )}
          >
            {ecrivent.length === 1
              ? t('inbox.team.typingOne', '{{name}} écrit…', { name: ecrivent[0] })
              : t('inbox.team.typingMany', '{{names}} écrivent…', { names: ecrivent.join(', ') })}
          </motion.p>
        )}
      </AnimatePresence>

      <SMSComposer
        phoneNumber={phoneNumber}
        channel={channel}
        darkMode={darkMode}
        onSent={handleSent}
        topSlot={channelPicker}
        fromNumber={line}
        mode={equipe ? mode : 'sms'}
        onTyping={signalerFrappe}
      />
    </div>
  )
}
